import { useEffect, useRef, useState } from "react";

const FILTER_SELECTION_APPLY_DELAY_MS =
  typeof process !== "undefined" && process.env.JEST_WORKER_ID != null ? 0 : 300;

function selectionsEqual(left, right) {
  return (
    left.length === right.length &&
    left.every((item, index) => Object.is(item, right[index]))
  );
}

export default function useDebouncedFilterSelection(
  value,
  onChange,
  delayMs = FILTER_SELECTION_APPLY_DELAY_MS
) {
  const initialValue = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState(initialValue);
  const latestDraftRef = useRef(initialValue);
  const pendingRef = useRef(null);
  const timerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const next = Array.isArray(value) ? value : [];
    const pending = pendingRef.current;
    if (pending && selectionsEqual(pending, next)) return;
    if (pending) {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = null;
      pendingRef.current = null;
    }
    latestDraftRef.current = next;
    setDraft(next);
  }, [value]);

  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      if (pendingRef.current) onChangeRef.current(pendingRef.current);
      pendingRef.current = null;
    },
    []
  );

  const schedule = (nextValue) => {
    const next = Array.isArray(nextValue) ? nextValue : [];
    latestDraftRef.current = next;
    pendingRef.current = next;
    setDraft(next);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      const pending = pendingRef.current;
      pendingRef.current = null;
      timerRef.current = null;
      if (pending) onChangeRef.current(pending);
    }, delayMs);
  };

  const flush = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = null;
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) onChangeRef.current(pending);
    return latestDraftRef.current;
  };

  const commitNow = (nextValue) => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = null;
    pendingRef.current = null;
    const next = Array.isArray(nextValue) ? nextValue : [];
    latestDraftRef.current = next;
    setDraft(next);
    onChangeRef.current(next);
  };

  return { draft, schedule, flush, commitNow };
}
